const COIN_SRC = '/assets/ui/coin-token.webp'
const FLIGHT_MS = 800
const FLIGHT_Z = 200

function prefersReducedMotion(): boolean {
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches
}

export function missionCardEl(missionId: string): HTMLElement | null {
  const card = document.querySelector<HTMLElement>(
    `[data-mission-id="${CSS.escape(missionId)}"]`,
  )
  if (!card) return null
  const claim = card.querySelector<HTMLElement>('.claim')
  if (claim) return claim
  const prize = card.querySelector<HTMLElement>('.coin-prize')
  return prize ?? card
}

/** Visible wallet chip in the top bar (wide, compact, or drawer). */
export function visibleCoinChip(): HTMLElement | null {
  const nodes = document.querySelectorAll<HTMLElement>('[data-coin-chip]')
  for (const node of nodes) {
    if (node.getAttribute('hidden') != null) continue
    const style = window.getComputedStyle(node)
    if (style.display === 'none' || style.visibility === 'hidden') continue
    const rect = node.getBoundingClientRect()
    if (rect.width > 0 && rect.height > 0) return node
  }
  return null
}

function pulseChip(chip: HTMLElement | null): void {
  if (!chip) return
  chip.classList.remove('is-crediting')
  void chip.offsetWidth
  chip.classList.add('is-crediting')
  window.setTimeout(() => chip.classList.remove('is-crediting'), 450)
}

/**
 * Flies a coin token from `fromEl` to the nav wallet chip. Resolves when the
 * animation finishes (or immediately if motion is reduced / no origin).
 */
export function flyCoinToWallet(
  fromEl: Element | null,
  delayMs = 0,
): Promise<void> {
  const origin = fromEl?.getBoundingClientRect()
  const destEl = visibleCoinChip()
  const dest = destEl?.getBoundingClientRect()
  if (!origin || origin.width === 0 || origin.height === 0) {
    pulseChip(destEl)
    return Promise.resolve()
  }
  if (prefersReducedMotion() || !dest || dest.width === 0) {
    pulseChip(destEl)
    return Promise.resolve()
  }

  const size = Math.max(28, Math.min(56, origin.width, origin.height))
  const startX = origin.left + origin.width / 2 - size / 2
  const startY = origin.top + origin.height / 2 - size / 2
  const endX = dest.left + dest.width / 2 - size / 2
  const endY = dest.top + dest.height / 2 - size / 2
  const spanY = Math.abs(startY - endY)
  const side = startX < endX ? -1 : 1
  const midX =
    startX + (endX - startX) * 0.5
    + side * Math.min(88, Math.max(36, spanY * 0.12))
  const midY = Math.min(
    Math.max(startY + (endY - startY) * 0.42, dest.bottom + 90),
    window.innerHeight - size - 12,
  )

  const img = document.createElement('img')
  img.src = COIN_SRC
  img.alt = ''
  img.setAttribute('aria-hidden', 'true')
  img.style.cssText = [
    'position:fixed',
    `left:${startX}px`,
    `top:${startY}px`,
    `width:${size}px`,
    `height:${size}px`,
    `z-index:${FLIGHT_Z}`,
    'pointer-events:none',
    'border-radius:50%',
    'will-change:transform',
    delayMs > 0 ? 'opacity:0' : 'opacity:1',
  ].join(';')
  document.body.appendChild(img)

  const easing = getComputedStyle(document.documentElement)
    .getPropertyValue('--md-motion-emphasized')
    .trim() || 'cubic-bezier(0.05, 0.7, 0.1, 1)'

  const anim = img.animate(
    [
      { opacity: 1, transform: 'translate(0, 0) scale(1) rotate(0deg)', offset: 0 },
      {
        transform:
          `translate(${midX - startX}px, ${midY - startY}px) scale(1.35) rotate(160deg)`,
        offset: 0.45,
      },
      {
        transform:
          `translate(${endX - startX}px, ${endY - startY}px) scale(0.45) rotate(280deg)`,
        offset: 1,
      },
    ],
    { duration: FLIGHT_MS, delay: delayMs, easing, fill: 'forwards' },
  )

  return anim.finished.then(
    () => {
      img.remove()
      pulseChip(destEl)
    },
    () => {
      img.remove()
      pulseChip(destEl)
    },
  )
}
